import { useCallback, useRef, useState } from 'react';

import captureStorageError from '../../../../utils/localStorage';

const PAST_DUE_THRESHOLD_STORAGE_KEY = 'acLeadDashboard.pastDueThresholdHours';
const AC_FILTER_STORAGE_KEY = 'acLeadDashboard.selectedACIds';
const HIDE_SUSPECT_STORAGE_KEY = 'acLeadDashboard.hideSuspect';
const STORAGE_ERROR_CONTEXT = 'AC Lead Dashboard filter';

/**
 * Reads a stored id list.
 *
 * @param {string} storageKey - Key to read.
 * @returns {Array|null} The stored ids, or null when nothing has been stored — which is not the
 *                       same as an empty selection the user deliberately made.
 */
const getStoredIds = (storageKey) => {
  try {
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      const parsed = JSON.parse(stored);

      if (Array.isArray(parsed)) {
        return parsed.filter((id) => typeof id === 'string');
      }
    }
  } catch (e) {
    captureStorageError('getItem', storageKey, e, STORAGE_ERROR_CONTEXT);
  }

  return null;
};

/**
 * Whether a reported selection leaves the stored one unchanged.
 *
 * Nothing stored and nothing selected counts as unchanged, so simply opening the dashboard does not
 * record an empty selection and suppress the default on the next visit.
 *
 * @param {Array|null} currentIds - What is stored, or null when nothing has been stored.
 * @param {Array} ids - The reported selection.
 * @returns {boolean} True when there is nothing to write.
 */
const checkIsSameSelection = (currentIds, ids) => (
  currentIds === null
    ? ids.length === 0
    : currentIds.length === ids.length && currentIds.every((id, index) => id === ids[index])
);

/**
 * Keeps a filter selection in browser local storage so it is reapplied when the dashboard is
 * reopened in the same browser. Nothing is stored server side, so the selection deliberately does
 * not follow the user across browsers or devices.
 *
 * @param {string} storageKey - Where to persist the selection.
 * @returns {object} { selectedIds, setSelectedIds } — an empty selection means no filter.
 */
const useStoredFilter = (storageKey) => {
  const [storedIds, setStoredIds] = useState(() => getStoredIds(storageKey));

  // Read through a ref so the setter below can compare against the current value while staying
  // referentially stable — see the loop it guards against.
  const storedIdsRef = useRef(storedIds);
  storedIdsRef.current = storedIds;

  const setSelectedIds = useCallback((ids) => {
    const currentIds = storedIdsRef.current;

    /*
     * Ignore a selection that changes nothing. MultiSelectFilter reports its selection from an
     * effect, including once on mount, so a filter hands back the value it was just given. Without
     * this guard that churns state on every render, and any callback derived from that state
     * re-fires the effect and loops until React gives up.
     */
    if (checkIsSameSelection(currentIds, ids)) {
      return;
    }

    setStoredIds(ids);

    try {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch (e) {
      captureStorageError('setItem', storageKey, e, STORAGE_ERROR_CONTEXT);
    }
  }, [storageKey]);

  return {
    selectedIds: storedIds ?? [],
    setSelectedIds,
  };
};

export default useStoredFilter;
export { AC_FILTER_STORAGE_KEY, HIDE_SUSPECT_STORAGE_KEY, PAST_DUE_THRESHOLD_STORAGE_KEY };
